import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, message, Spin } from 'antd';
import styled from 'styled-components';
import queryString from 'query-string';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import { getSearchProperties } from 'utils/apis/properties';
import { SEARCH_MODE_BY_STATE, SEARCH_MODE_BY_ADDRESS, DEFAULT_MAP_LOCATION } from 'utils/constants';
import { getAddressComponentLongName } from 'utils/general';

import Card from 'components/Card/Card';
import GoogleMap from './components/GoogleMap/GoogleMap';
import SearchCard from './components/SearchCard/SearchCard';

import BackgroundImage from './images/bg.svg';

const ContentCard = styled(Card)`
  flex-direction: column;
  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

const RootContainer = styled(Row)`
  background-image: url(${BackgroundImage});
  min-height: 100vh;
  background-size: cover;
`;

class SearchHost extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filteredProperties: [],
      isMapLoading: true,
      mode: '',
      defaultAddress: '',
      selectedPlace: {},
      selectedState: '',
      selectedStatus: ''
    };
  }
  map = undefined;

  async componentDidMount() {
    const addressQueryString = this.getAddressQueryString();
    this.setDisplayAddress(addressQueryString);
    if (addressQueryString) {
      await this.fetchPropertiesWithAddress({ address: addressQueryString });
    } else {
      await this.fetchProperties(DEFAULT_MAP_LOCATION);
    }
    this.setState({
      mode: SEARCH_MODE_BY_ADDRESS,
      isMapLoading: false
    });
  }

  getAddressQueryString = () => {
    const { location } = this.props;
    if (location && location.search) {
      const searchQuery = queryString.parse(location.search);
      if (searchQuery && searchQuery.address) {
        return searchQuery.address;
      }
    }
    return '';
  };

  setDisplayAddress = address => {
    this.setState({ defaultAddress: address });
  };

  fetchPropertiesWithAddress = async ({ address, state, isFindAll, regulationStatus }) => {
    const location = await this.getLocation(address);
    this.setState({
      defaultAddress: address || ''
    });
    return this.fetchProperties({ lat: location.lat, lng: location.lng, state, isFindAll, regulationStatus });
  };

  fetchProperties = async ({ lat, lng, state, isFindAll, regulationStatus }) => {
    const query = {
      longitude: lng,
      latitude: lat,
      state,
      isFindAll,
      regulationStatus
    };
    const formattedPropertyData = await getSearchProperties(query)
      .then(propertyData => this.getFormattedPropertyData(propertyData))
      .catch(e => {
        console.error(e);
        message.error('Error while fetching properties');
        return [];
      });

    const filteredProperties = formattedPropertyData.filter(property => property.longitude && property.latitude);

    this.setState({
      filteredProperties,
      mapCenter: lat && lng ? { lat, lng } : DEFAULT_MAP_LOCATION,
      mapZoom: 12,
      selectedPlace: { latitude: lat, longitude: lng }
    });
    return filteredProperties;
  };

  getLocation = async address => {
    return geocodeByAddress(address)
      .then(results => getLatLng(results[0]))
      .catch(ex => {
        console.warn(`geocodeByAddress failed for address ${address}`);
        console.warn(ex);
        return DEFAULT_MAP_LOCATION;
      });
  };

  getFormattedPropertyData = (propertyData = []) => {
    return propertyData.map(property => ({
      latitude: property.latitude,
      longitude: property.longitude,
      propertyStatus: property.regulationStatus,
      propertyName: property.name,
      host: {
        verified: property.hosts ? property.hosts.filter(host => host.status === 'verified').length : 0, // TODO: Find a better way for hard-coded constants
        unverified: property.hosts ? property.hosts.filter(host => host.status === 'unverified').length : 0 // TODO: Find a better way for hard-coded constants
      },
      hosts: property.hosts,
      id: property._id,
      state: property.state,
      streetAddress: property.streetAddress,
      city: property.city,
      country: property.country,
      zipCode: property.zipcode,
      propertyIsFromNebula: property.source === 'hostplatform', // TODO: Find a better way for hard-coded constants
      isOpen: false
    }));
  };

  getFilteredProperties = async (mode, selectedPlace, selectedState, selectedStatus) => {
    let filteredProperties = [];
    if (mode === SEARCH_MODE_BY_ADDRESS) {
      filteredProperties = await this.fetchProperties({
        ...(selectedPlace && selectedPlace.latitude && selectedPlace.longitude && { lat: selectedPlace.latitude }),
        ...(selectedPlace && selectedPlace.latitude && selectedPlace.longitude && { lng: selectedPlace.longitude }),
        ...(selectedStatus && selectedStatus !== 'All' && { regulationStatus: selectedStatus })
      });
    } else if (mode === SEARCH_MODE_BY_STATE) {
      filteredProperties = await this.fetchProperties({
        ...(selectedState && selectedState !== 'All' ? { state: selectedState } : { isFindAll: true }),
        ...(selectedStatus && selectedStatus !== 'All' && { regulationStatus: selectedStatus })
      });
    }
    return filteredProperties;
  };

  handleOnAirbnbFilter = async selectedStatus => {
    const { mode, selectedPlace, selectedState } = this.state;
    this.setState({ isMapLoading: true });
    const filteredProperties = await this.getFilteredProperties(mode, selectedPlace, selectedState, selectedStatus);
    this.setState({
      mapCenter:
        selectedPlace && selectedPlace.latitude && selectedPlace.longitude
          ? { lat: selectedPlace.latitude, lng: selectedPlace.longitude }
          : DEFAULT_MAP_LOCATION,
      mapZoom: 12,
      isMapLoading: false,
      filteredProperties,
      selectedStatus: selectedStatus !== 'All' ? selectedStatus : ''
    });
  };

  handleOnLocationSearch = async query => {
    const { selectedStatus } = this.state;
    const { mode, place, selectedPlaceCoordinate, selectedState } = query;
    this.setState({ isMapLoading: true, mode });
    const filteredProperties = await this.getFilteredProperties(mode, selectedPlaceCoordinate, selectedState, selectedStatus);
    this.setState({
      filteredProperties,
      mapCenter: { lat: selectedPlaceCoordinate.latitude, lng: selectedPlaceCoordinate.longitude },
      mapZoom: mode === SEARCH_MODE_BY_STATE ? 10 : 15,
      selectedPlace: {
        name: place && place.name,
        address: place && place.formatted_address,
        zipCode: getAddressComponentLongName(place, 'postal_code'),
        state: getAddressComponentLongName(place, 'administrative_area_level_1'),
        city: getAddressComponentLongName(place, 'locality'),
        country: getAddressComponentLongName(place, 'country'),
        ...selectedPlaceCoordinate
      },
      isMapLoading: false
    });
  };

  handleOnEnquireClick = e => {
    const { history } = this.props;
    const { selectedPlace } = this.state;
    e.preventDefault();
    const enquiryFormPath = '/enquiry';
    if (selectedPlace && selectedPlace.name && selectedPlace.address) {
      const historyState = { place: selectedPlace, address: `${selectedPlace.name}, ${selectedPlace.address}` };
      history.push(enquiryFormPath, historyState);
    } else {
      history.push(enquiryFormPath);
    }
  };

  handleOnPropertyEnquireClick = selectedPropertyId => {
    const { history } = this.props;
    const { filteredProperties } = this.state;
    const selectedProperty = filteredProperties.find(property => String(property.id) === String(selectedPropertyId));
    const enquiryFormPath = '/enquiry';
    if (selectedProperty && selectedProperty.propertyName && selectedProperty.streetAddress) {
      const historyState = {
        place: {
          name: selectedProperty.propertyName,
          address: selectedProperty.streetAddress,
          zipCode: selectedProperty.zipCode,
          state: selectedProperty.state,
          city: selectedProperty.city,
          country: selectedProperty.country
        },
        address: `${selectedProperty.propertyName}, ${selectedProperty.streetAddress}`
      };
      history.push(enquiryFormPath, historyState);
    } else {
      history.push(enquiryFormPath);
    }
  };

  getNumberOfPropertiesAndHosts = (properties = []) => ({
    hosts: properties.reduce((totalNoOfHost, property) => {
      const currentNoOfHost = property.hosts && Array.isArray(property.hosts) ? property.hosts.length : 0;
      return totalNoOfHost + currentNoOfHost;
    }, 0),
    properties: properties.length
  });

  render() {
    const { className } = this.props;
    const { filteredProperties, isMapLoading, mapCenter, mapZoom, mode, defaultAddress } = this.state;
    const { hosts: numberOfHost, properties: numberOfProperties } = this.getNumberOfPropertiesAndHosts(filteredProperties);
    return (
      <RootContainer className={className} type="flex" justify="center" align="middle">
        <Col span={24} lg={22} xxl={16}>
          <ContentCard>
            <Col span={24} lg={9}>
              <SearchCard
                numberOfHost={numberOfHost}
                numberOfProperties={numberOfProperties}
                mode={mode}
                onAirbnbFilter={this.handleOnAirbnbFilter}
                onEnquireClick={this.handleOnEnquireClick}
                onSearch={this.handleOnLocationSearch}
                setDisplayAddress={this.setDisplayAddress}
                defaultAddress={defaultAddress}
              />
            </Col>
            <Col span={24} lg={15}>
              <Spin delay={500} size="large" spinning={isMapLoading}>
                <GoogleMap
                  properties={filteredProperties}
                  googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyAHBWhCJOD2ow59B91tQWXL_lunDkacQXE&v=3.exp&libraries=geometry,drawing,places"
                  loadingElement={<div style={{ height: `100%` }} />}
                  containerElement={<div style={{ height: `700px` }} />}
                  mapElement={<div style={{ height: `100%` }} />}
                  center={mapCenter}
                  zoom={mapZoom}
                  innerRef={map => {
                    this.map = map;
                  }}
                  onPropertyEnquireClick={this.handleOnPropertyEnquireClick}
                />
              </Spin>
            </Col>
          </ContentCard>
        </Col>
      </RootContainer>
    );
  }
}

SearchHost.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object
};

SearchHost.defaultProps = {
  locaiton: {},
  history: {}
};

export default SearchHost;
